import { Platform, StyleSheet } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';

const isIOS = Platform.OS === 'ios';
const isWeb = Platform.OS === 'web';

/**
 * Shared style tokens for the three new ASN Gap "Overview" analytics
 * components (ASNLoadsComparison, BottomPerformingNonASN, NonASNLoadTrend).
 *
 * Mirrors the card/typography/spacing conventions already established by
 * `PTExecutionSectionStyles.ts` (background #FFFFFF, borderRadius 8, the
 * same soft drop shadow, the same title/subtitle type scale) so the new
 * Overview section feels consistent with the existing Perfect Truck
 * Analytics "Overview" tab, without importing/mutating that file.
 */
export const asnOverviewChartsStyles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    paddingVertical: isIOS ? 16 : 20,
    paddingHorizontal: isIOS ? 20 : 24,
    rowGap: 4,
  },
  fullWidthCard: {
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  headerTextGroup: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    flexShrink: 1,
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    color: '#1A1D21',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  subTitle: {
    marginLeft: 4,
    fontSize: 12,
    lineHeight: 16,
    color: '#838383',
    fontFamily: FontFamily.regular,
    fontWeight: '400',
  },
  // ── Mobile-only header layout ────────────────────────────────────────────
  // Two-column row: left side stacks the title + subtitle-icon row; right
  // side optionally holds an action button (e.g. Back during drill-down).
  // Used by all three Overview cards on mobile.
  mobileHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  // Left column — expands to fill available width, leaving room for the
  // optional right-side button.
  mobileHeaderLeft: {
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  // Subtitle + info icon side-by-side on one line, vertically centred.
  mobileSubtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap' as const,
    marginTop: 2,
  },
  // Back button pinned to the top-right of the mobile header (ASN chart only).
  mobileBackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 3,
    paddingVertical: 2,
    paddingLeft: 6,
    flexShrink: 0,
  },
  mobileBackButtonText: {
    fontSize: 12,
    color: '#0B5FC6',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  infoIconWrap: {
    marginLeft: 6,
  },
  description: {
    fontSize: 12,
    lineHeight: 17,
    color: '#6A6D71',
    fontFamily: FontFamily.regular,
    marginBottom: 14,
  },
  // On web, the description text and sort dropdown sit side-by-side in one row
  // directly beneath the title. The text takes all remaining horizontal space
  // (flex: 1) while the dropdown is fixed-width and right-aligned.
  descriptionSortRow: {
    ...(isWeb ? {
      flexDirection: 'row' as const,
      alignItems: 'flex-start',
      columnGap: 16,
      marginBottom: 14,
    } : {}),
  },
  // Gives the description text flex growth so it fills the space left of the
  // dropdown. Only applied on web (inside descriptionSortRow).
  descriptionFlex: {
    ...(isWeb ? { flex: 1, marginBottom: 0 } : {}),
  },
  // Web-only: wraps the headerRow + description+dropdown row of each side-by-side
  // card so both sections occupy the same vertical space and the content below
  // (table / chart bars) always starts at the same horizontal line.
  // minHeight = title row (~24 px) + description row (~34 px) + gap = ~70 px.
  // Mobile: no height constraint — natural flow is preserved.
  webCardHeaderSection: {
    ...(isWeb ? { minHeight: 90 } : {}),
  },

  // ── Legend (ASNLoadsComparison) ─────────────────────────────────────────
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 18,
    rowGap: 6,
    marginBottom: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 6,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },
  legendMarker: {
    width: 2,
    height: 14,
    backgroundColor: '#1A1D21',
  },
  legendText: {
    fontSize: 12,
    lineHeight: 16,
    color: '#4B5563',
    fontFamily: FontFamily.medium,
    fontWeight: '500',
  },

  // ── Stacked bar rows ─────────────────────────────────────────────────────
  barList: {
    rowGap: 10,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
  },
  barRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 6,
    marginBottom: 2,
  },
  barRowTotal: {
    marginTop: 6,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E3E9F3',
  },
  barRowLabel: {
    width: 110,
    flexShrink: 0,
    fontSize: 13,
    color: '#3A3A3A',
    fontFamily: FontFamily.medium,
    fontWeight: '500',
    textAlign: 'right',
  },
  barRowLabelTablet: {
    width: 84,
  },
  barRowLabelMobile: {
    width: 80,
    flexShrink: 0,
    textAlign: 'left',
  },
  barRowLabelTotal: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  barTrackWrap: {
    flex: 1,
    position: 'relative',
    justifyContent: 'center',
    minWidth: 0,
  },
  barTrack: {
    flexDirection: 'row',
    width: '100%',
    height: isIOS ? 26 : 30,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#E9ECEF',
  },
  barSegmentNonAsn: {
    height: '100%',
    justifyContent: 'center',
    paddingHorizontal: 8,
    minWidth: 2,
  },
  barSegmentNonAsnBlue: {
    backgroundColor: '#0B5FC6',
  },
  barSegmentNonAsnRed: {
    backgroundColor: '#C12C01',
  },
  barSegmentAsn: {
    flex: 1,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingHorizontal: 8,
    backgroundColor: '#C8CDD7',
    minWidth: 0,
  },
  barSegmentAsnGray: {
    backgroundColor: '#C8CDD7',
  },
  barSegmentLabel: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  barSegmentLabelDark: {
    color: '#374151',
  },
  barOutsideLabel: {
    position: 'absolute',
    fontSize: 12,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    marginLeft: 6,
  },
  barTargetMarker: {
    position: 'absolute',
    top: -3,
    bottom: -3,
    width: 2,
    backgroundColor: '#1A1D21',
  },
  barRowPct: {
    width: 64,
    flexShrink: 0,
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    textAlign: 'right',
  },
  barRowPctMobile: {
    width: 60,
    flexShrink: 0,
    textAlign: 'left',
  },
  pctBlue: {
    color: '#0B5FC6',
  },
  pctRed: {
    color: '#C12C01',
  },

  // ── Empty / loading states ──────────────────────────────────────────────
  emptyContainer: {
    width: '100%',
    minHeight: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#6A6D71',
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 14,
    color: '#C12C01',
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 10,
    alignSelf: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#0B5FC6',
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },

  // ── Lower section layout (Bottom Performing + Trend side by side) ──────
  // Web: fixed 50/50 split, never wraps — the two cards sit side by side at
  // exactly half the container width. Mobile: full-width stack (no flexDirection
  // row, no fixed width) so each card takes the full width and stacks vertically.
  lowerSectionRow: {
    width: '100%',
    ...(isWeb
      ? {
          flexDirection: 'row' as const,
          flexWrap: 'nowrap' as const,
          columnGap: 12,
          alignItems: 'stretch' as const,
        }
      : {
          flexDirection: 'column' as const,
          rowGap: 12,
        }),
  },
  lowerSectionColumn: {
    ...(isWeb
      ? {
          // Each column occupies exactly half the row minus half the gap.
          // flexBasis: '50%' alone would ignore the gap; using calc is not
          // available in RN, so we set flexBasis to '50%' and let flexShrink
          // account for the gap — the 12 px gap is small enough that 50%
          // effectively gives an even split without overflow.
          flexBasis: '50%' as any,
          flexGrow: 0,
          flexShrink: 1,
          minWidth: 0,
        }
      : {
          // Mobile: full width, stacked
          width: '100%' as const,
          flexGrow: 0,
          flexShrink: 0,
        }),
  },
  stretchCard: {
    flex: 1,
  },

  // ── Bottom Performing table ─────────────────────────────────────────────
  tableWrap: {
    width: '100%',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
    paddingBottom: 8,
    marginBottom: 4,
  },
  tableHeaderCellRank: {
    width: 28,
    flexShrink: 0,
  },
  tableHeaderCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 2,
    paddingHorizontal: 6,
  },
  tableHeaderCellRight: {
    justifyContent: 'flex-end',
  },
  tableHeaderText: {
    fontSize: 11,
    letterSpacing: 0.3,
    color: '#6A6D71',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F4F9',
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  rankBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FDEDEA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rankBadgeText: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#C12C01',
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 6,
  },
  tableCellRight: {
    alignItems: 'flex-end',
  },
  tableCellLabel: {
    fontSize: 13,
    color: '#0B5FC6',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  tableCellValue: {
    fontSize: 13,
    color: '#1A1D21',
    fontFamily: FontFamily.medium,
    fontWeight: '500',
    textAlign: 'right',
  },
  tableCellPct: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    textAlign: 'right',
  },
  tableFooterText: {
    marginTop: 10,
    fontSize: 12,
    color: '#8A8F98',
    fontFamily: FontFamily.regular,
  },

  // ── Trend chart ──────────────────────────────────────────────────────────
  trendChartContainer: {
    width: '100%',
    minHeight: isIOS ? 220 : 260,
    rowGap: 6,
  },

  // ── ASNLoadsComparison drill-down controls (back link + sort/search) ────
  // drillControlsRow is kept for mobile; web uses the inline title-row Back button.
  drillControlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: 8,
    columnGap: 12,
    marginBottom: 10,
  },
  backLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 4,
    paddingVertical: 4,
    paddingRight: 8,
  },
  backLinkText: {
    fontSize: 13,
    color: '#0B5FC6',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  breadcrumbText: {
    fontSize: 12,
    color: '#8A8F98',
    fontFamily: FontFamily.regular,
    marginLeft: 4,
  },

  // ── Web-only: title row with inline Back button ──────────────────────────
  // The title group sits on the left (flex: 1, shrinks freely) and the Back
  // button is pinned to the right. flexWrap is off so they never stack — if
  // the title is very long it truncates rather than pushing the button down.
  webTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap' as const,
    marginBottom: 4,
    columnGap: 8,
  },
  // Title group inside webTitleRow: shrinks freely, never forces the Back
  // button off-screen.
  webTitleGroup: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    flexShrink: 1,
    flex: 1,
    minWidth: 0,
  },
  // Compact Back button — icon + short label, anchored to the right of the
  // title row. flexShrink: 0 keeps it from being squeezed by a long title.
  webBackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 3,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 0,
    borderWidth: 0,
    borderColor: '#C7D9F5',
    backgroundColor: '#fefefe',
    flexShrink: 0,
  },
  webBackButtonText: {
    fontSize: 12,
    color: '#0B5FC6',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },

  chartControlsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 10,
    flexWrap: 'wrap',
    rowGap: 8,
    flex: 1,
    justifyContent: 'flex-end',
  },
  sortDropdownWrap: {
    minWidth: 155,
    maxWidth: '100%',
    flex: 0,
    flexShrink: 1,
  },
  searchInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 6,
    borderWidth: 1,
    borderColor: '#D8D8D8',
    borderRadius: 6,
    paddingHorizontal: 10,
    height: isIOS ? 34 : 36,
    minWidth: 180,
    backgroundColor: '#FFFFFF',
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#1A1D21',
    fontFamily: FontFamily.regular,
    paddingVertical: 0,
  },
  dloDropdownContainer: {
    width: '100%',
    gap: 0,
  },
});

export default asnOverviewChartsStyles;
